import { type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthProvider";
import { localStorageService } from "../../../services/LocalStorageService";
import type { DraggableFabOffset } from "../../../utils/models";

type Args = {
	/** When false the hook is inert: the offset stays zero and no listeners are attached. */
	enabled: boolean;
	/** Per-FAB storage key; the saved offset is scoped to this key and the signed-in user. */
	storageKey: string | undefined;
	/** FAB diameter (px) — needed to keep the button inside the viewport. */
	size: number;
	/** The FAB's anchored offsets (px) from the viewport's bottom-right corner. */
	bottom: number;
	right: number;
};

type Result = {
	offset: DraggableFabOffset;
	isDragging: boolean;
	dragHandlers: {
		onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
		onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
		onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
		/** An interrupted gesture (e.g. a system touch takeover) ends the drag where it stands. */
		onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
		/** Suppresses the browser's own drag-and-drop, which would otherwise eat the gesture. */
		onDragStart: (event: ReactDragEvent<HTMLElement>) => void;
	};
	/** True when the click that follows was the tail of a drag, so the caller should swallow it. */
	consumeDragClick: () => boolean;
};

const ZERO_OFFSET: DraggableFabOffset = { x: 0, y: 0 };
/** Gap (px) kept between the FAB and every viewport edge so it can never be dragged out of reach. */
const EDGE_MARGIN = 8;
/** Pointer travel (px) past which the gesture counts as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

const clampToViewport = (offset: DraggableFabOffset, { size, bottom, right }: Pick<Args, "size" | "bottom" | "right">): DraggableFabOffset => {
	const anchorLeft = window.innerWidth - right - size;
	const anchorTop = window.innerHeight - bottom - size;
	return {
		x: Math.min(Math.max(offset.x, EDGE_MARGIN - anchorLeft), window.innerWidth - size - EDGE_MARGIN - anchorLeft),
		y: Math.min(Math.max(offset.y, EDGE_MARGIN - anchorTop), window.innerHeight - size - EDGE_MARGIN - anchorTop),
	};
};

/**
 * Lets a fixed-position FAB be dragged anywhere in the viewport, remembering where the user
 * dropped it (per user, per `storageKey`). The position is kept as a delta from the FAB's
 * anchored corner, so the component's own `bottom`/`right` props stay the resting place and
 * a cleared offset restores the default layout. The offset is re-clamped on restore and on
 * resize, so a smaller window can never strand the button off-screen.
 */
export const useDraggableFabPosition = ({ enabled, storageKey, size, bottom, right }: Args): Result => {
	const { userInfo } = useAuth();
	const userId = userInfo?.id ?? "";
	const [offset, setOffset] = useState<DraggableFabOffset>(ZERO_OFFSET);
	const [isDragging, setIsDragging] = useState(false);
	// The pointer handlers read/write the live offset outside React's render cycle.
	const offsetRef = useRef<DraggableFabOffset>(ZERO_OFFSET);
	const dragRef = useRef<{
		pointerId: number;
		startX: number;
		startY: number;
		startOffset: DraggableFabOffset;
		moved: boolean;
	} | null>(null);
	const suppressClickRef = useRef(false);

	const applyOffset = useCallback((next: DraggableFabOffset) => {
		offsetRef.current = next;
		setOffset(next);
	}, []);

	// `useAuth` populates userInfo asynchronously, so the restore is an effect rather than lazy
	// initial state (mirrors useColumnOrder). Re-runs harmlessly if the user changes.
	useEffect(() => {
		if (!enabled || !storageKey || !userId) return;
		const stored = localStorageService.getDraggableFabOffset(storageKey, userId);
		if (stored) applyOffset(clampToViewport(stored, { size, bottom, right }));
	}, [enabled, storageKey, userId, size, bottom, right, applyOffset]);

	useEffect(() => {
		if (!enabled) return;
		const handleResize = () => applyOffset(clampToViewport(offsetRef.current, { size, bottom, right }));
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [enabled, size, bottom, right, applyOffset]);

	const onPointerDown = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (!enabled || event.button !== 0) return;
			dragRef.current = {
				pointerId: event.pointerId,
				startX: event.clientX,
				startY: event.clientY,
				startOffset: offsetRef.current,
				moved: false,
			};
			// Capture so the drag survives the pointer leaving the (small) button.
			event.currentTarget.setPointerCapture(event.pointerId);
		},
		[enabled],
	);

	const onPointerMove = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			const drag = dragRef.current;
			if (!drag || drag.pointerId !== event.pointerId) return;
			const deltaX = event.clientX - drag.startX;
			const deltaY = event.clientY - drag.startY;
			if (!drag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;
			drag.moved = true;
			setIsDragging(true);
			applyOffset(clampToViewport({ x: drag.startOffset.x + deltaX, y: drag.startOffset.y + deltaY }, { size, bottom, right }));
		},
		[size, bottom, right, applyOffset],
	);

	const onPointerUp = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			const drag = dragRef.current;
			if (!drag || drag.pointerId !== event.pointerId) return;
			dragRef.current = null;
			if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
			if (!drag.moved) return;
			setIsDragging(false);
			// A drag always ends in a click on the button; that click must not open the chat.
			suppressClickRef.current = true;
			if (storageKey && userId) localStorageService.setDraggableFabOffset(storageKey, offsetRef.current, userId);
		},
		[storageKey, userId],
	);

	// An <img> or text glyph inside the FAB is natively draggable, and the browser's drag-and-drop
	// outranks pointer events: it fires `pointercancel`, releases the capture, and the FAB freezes
	// one step from where it started while a ghost image follows the cursor. Cancelling `dragstart`
	// is what keeps the gesture ours.
	const onDragStart = useCallback((event: ReactDragEvent<HTMLElement>) => event.preventDefault(), []);

	const consumeDragClick = useCallback(() => {
		if (!suppressClickRef.current) return false;
		suppressClickRef.current = false;
		return true;
	}, []);

	return {
		offset,
		isDragging,
		dragHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onDragStart },
		consumeDragClick,
	};
};
